/**
 * 個別説明「実施状況」タブ (2026-10-07)。管理者 (部署「管理者」+ オーナー) のみ表示。
 *
 * 外部SA から届く請求書と実際の個別説明を突き合わせるための一覧。
 * 商談ステージに依存せず、申込があった個別説明を 1 行 = 1 件で表示する (行の組み立ては briefingStatus.ts)。
 *
 * - 年月・担当SA・状態・SA請求・検索は URL (?month= &sa= &state= &bill= &q=) に持つ。
 *   記録ページへ移動して戻ると同じ絞り込み・同じ行に戻る (BriefingRecordPage が state.from へ戻す)
 * - 検索欄に請求番号を入れると、年月に関係なくその請求書に含まれる個別説明だけを表示する
 *   (SA の請求書は未請求の個別説明を月に関係なくまとめて作られるため)
 * - 書き出しは顧客リスト・フォーム回収一覧と同じ方式 (表示中の行を CSV / Excel)
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7.9
 */
import { COMMISSION_INVOICE_STATUS_LABELS } from '@goenhub/shared';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  BRIEFING_BILLING_LABEL,
  BRIEFING_ROW_STATE_LABEL,
  buildBriefingStatusRows,
  invoiceBriefingSummary,
  type BriefingBillingKind,
  type BriefingRowState,
  type BriefingStatusRow,
} from './briefingStatus';
import { exportBriefingStatusCsv, exportBriefingStatusXlsx } from './briefingStatusExport';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAdminDirectory } from '@/hooks/useAdmins';
import { useAllBriefingRecords } from '@/hooks/useBriefings';
import { useCommissionInvoices } from '@/hooks/useCommissionInvoices';
import { useCommissionOverrides } from '@/hooks/useCommissionOverrides';
import { useCommissionSets } from '@/hooks/useCommissions';
import { useDeals } from '@/hooks/useDeals';
import { useMailContacts } from '@/hooks/useMailContacts';
import { toDate } from '@/lib/firestoreTime';

const ALL = 'all';
/** 担当SA「未定」(日程未確定) */
const NONE = 'none';

type StateFilter = BriefingRowState | typeof ALL;
type BillFilter = Exclude<BriefingBillingKind, 'none'> | typeof ALL;

const STATE_ORDER: BriefingRowState[] = [
  'completed',
  'draft',
  'not_conducted',
  'no_record',
  'unscheduled',
];
const BILL_ORDER: Exclude<BriefingBillingKind, 'none'>[] = [
  'unbilled',
  'billed',
  'paid',
  'not_applicable',
];

const STATE_BADGE: Record<BriefingRowState, Parameters<typeof Badge>[0]['variant']> = {
  completed: 'approved',
  draft: 'draft',
  not_conducted: 'danger',
  no_record: 'review',
  unscheduled: 'default',
};

function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftMonth(key: string, delta: number): string {
  const [y, m] = key.split('-').map(Number);
  return monthKeyOf(new Date(y, m - 1 + delta, 1));
}
function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${y}年${m}月`;
}
function fmt(d: Date | null): string {
  if (!d) return '—';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function fmtMd(v: unknown): string {
  const d = toDate(v);
  return d ? `${d.getMonth() + 1}/${d.getDate()}` : '';
}

const GRID =
  'grid grid-cols-[84px_84px_84px_minmax(150px,1.2fr)_minmax(160px,1.4fr)_minmax(110px,1fr)_92px_minmax(130px,1fr)_92px] gap-3';

interface BriefingStatusTabProps {
  /** 記録ページから戻った時に位置を合わせて強調する行 */
  focusRowKey: string | null;
  onFocusConsumed: () => void;
}

export function BriefingStatusTab({ focusRowKey, onFocusConsumed }: BriefingStatusTabProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const month = params.get('month') || monthKeyOf(new Date());
  const saFilter = params.get('sa') || ALL;
  const stateFilter = (params.get('state') || ALL) as StateFilter;
  const billFilter = (params.get('bill') || ALL) as BillFilter;
  const qParam = params.get('q') ?? '';

  const setParam = (key: string, value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === ALL) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  // 検索欄は入力のたびに URL を書き換えず、少し待ってから反映する
  const [qInput, setQInput] = useState(qParam);
  useEffect(() => {
    setQInput(qParam);
  }, [qParam]);
  useEffect(() => {
    if (qInput === qParam) return;
    const t = setTimeout(() => setParam('q', qInput.trim() ? qInput : null), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  // ─── データ ───
  const deals = useDeals({ includeDeleted: true });
  const records = useAllBriefingRecords();
  const invoices = useCommissionInvoices();
  const sets = useCommissionSets();
  const activeSetId = useMemo(
    () => (sets.data ?? []).find((s) => s.isActive)?.id ?? null,
    [sets.data],
  );
  const overrides = useCommissionOverrides(activeSetId);
  const { data: admins } = useAdminDirectory();
  const { data: contacts } = useMailContacts();

  const adminByUid = useMemo(() => new Map((admins ?? []).map((a) => [a.uid, a])), [admins]);
  const contactById = useMemo(() => new Map((contacts ?? []).map((c) => [c.id, c])), [contacts]);
  const feeByUid = useMemo(
    () => new Map((overrides.data ?? []).map((o) => [o.id, o.briefingFee ?? 0])),
    [overrides.data],
  );

  const isLoading =
    deals.isLoading ||
    records.isLoading ||
    invoices.isLoading ||
    sets.isLoading ||
    (!!activeSetId && overrides.isLoading);
  const loadError = deals.error ?? records.error ?? invoices.error ?? null;

  const allRows = useMemo<BriefingStatusRow[]>(() => {
    if (!deals.data || !records.data || !invoices.data) return [];
    return buildBriefingStatusRows({
      deals: deals.data,
      records: records.data,
      invoices: invoices.data,
      isExternal: (uid) => adminByUid.get(uid)?.level === 'external',
      briefingFeeOf: (uid) => feeByUid.get(uid) ?? 0,
    });
  }, [deals.data, records.data, invoices.data, adminByUid, feeByUid]);

  const contactName = (id: string | null) => (id && contactById.get(id)?.name) || '(顧客名未確定)';
  const saName = (uid: string | null) => {
    if (!uid) return '未定';
    const a = adminByUid.get(uid);
    return a?.fullName || a?.displayName || '(不明なユーザー)';
  };
  const isInternal = (uid: string) => adminByUid.get(uid)?.level !== 'external';

  // 検索欄が請求番号と一致したら「請求書で探す」状態
  const invoice = useMemo(() => {
    const q = qParam.trim().toUpperCase();
    if (!q) return null;
    return (invoices.data ?? []).find((i) => i.invoiceNumber.toUpperCase() === q) ?? null;
  }, [qParam, invoices.data]);

  const monthRows = useMemo(() => allRows.filter((r) => r.monthKey === month), [allRows, month]);

  // 状態・SA請求の件数を数える母集団 (年月・担当SA・検索まで適用)
  const baseRows = useMemo(() => {
    if (invoice) return allRows.filter((r) => r.billing.invoiceId === invoice.id);
    let rows = monthRows;
    if (saFilter === NONE) rows = rows.filter((r) => !r.saUid);
    else if (saFilter !== ALL) rows = rows.filter((r) => r.saUid === saFilter);
    const q = qParam.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [contactName(r.contactId), r.dealTitle, saName(r.saUid), r.billing.invoiceNumber ?? '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, allRows, monthRows, saFilter, qParam, contactById, adminByUid]);

  const visibleRows = useMemo(() => {
    if (invoice) return baseRows;
    return baseRows.filter(
      (r) =>
        (stateFilter === ALL || r.state === stateFilter) &&
        (billFilter === ALL || r.billing.kind === billFilter),
    );
  }, [invoice, baseRows, stateFilter, billFilter]);

  // 担当SA別の件数 (その月、担当SA の絞り込み前)
  const saSummary = useMemo(() => {
    const map = new Map<string, BriefingStatusRow[]>();
    for (const r of monthRows) {
      const k = r.saUid ?? NONE;
      map.set(k, [...(map.get(k) ?? []), r]);
    }
    return [...map.entries()]
      .map(([uid, rows]) => ({ uid, rows }))
      .sort((a, b) => (a.uid === NONE ? 1 : b.uid === NONE ? -1 : b.rows.length - a.rows.length));
  }, [monthRows]);

  // 年月の選択肢: データのある月 + 今月 + 選択中の月
  const monthOptions = useMemo(() => {
    const set = new Set(allRows.map((r) => r.monthKey).filter(Boolean));
    set.add(monthKeyOf(new Date()));
    set.add(month);
    return [...set].sort().reverse();
  }, [allRows, month]);

  const saOptions = useMemo(() => {
    const uids = [...new Set(allRows.map((r) => r.saUid).filter((v): v is string => !!v))];
    return uids
      .map((uid) => ({ uid, label: saName(uid) + (isInternal(uid) ? '（社内）' : '') }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, adminByUid]);

  // ─── 記録ページから戻った時: 行の位置に合わせて一瞬強調 ───
  const [flashKey, setFlashKey] = useState<string | null>(null);
  useEffect(() => {
    if (!focusRowKey || isLoading) return;
    const el = document.getElementById(`bs-row-${focusRowKey}`);
    el?.scrollIntoView({ block: 'center' });
    setFlashKey(focusRowKey);
    onFocusConsumed();
    const t = setTimeout(() => setFlashKey(null), 2400);
    return () => clearTimeout(t);
  }, [focusRowKey, isLoading, onFocusConsumed]);

  const openRecord = (row: BriefingStatusRow) => {
    navigate(`/briefings/record/${row.dealId}`, {
      state: { from: `${location.pathname}${location.search}`, rowKey: row.key },
    });
  };

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (visibleRows.length === 0) {
      toast.error('書き出し対象がありません');
      return;
    }
    const resolvers = { contactName, saName };
    const label = invoice ? invoice.invoiceNumber : month;
    try {
      if (format === 'csv') exportBriefingStatusCsv(visibleRows, resolvers, label);
      else exportBriefingStatusXlsx(visibleRows, resolvers, label);
      toast.success(`${visibleRows.length} 件を書き出しました`);
    } catch (err) {
      toast.error(`書き出し失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;
  if (loadError) {
    return (
      <div className="rounded-md border border-[var(--accent-red)] bg-[var(--accent-red-subtle)] p-4 text-sm text-[var(--accent-red)]">
        実施状況を読み込めませんでした:{' '}
        {loadError instanceof Error ? loadError.message : String(loadError)}
      </div>
    );
  }

  const chip = (active: boolean, label: string, count: number | null, onClick: () => void) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)] font-semibold'
          : 'border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      {label}
      {count !== null && (
        <span
          className={`tabular-nums text-[11px] ${active ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
        >
          {count}
        </span>
      )}
    </button>
  );

  const renderBilling = (row: BriefingStatusRow) => {
    const b = row.billing;
    if (b.kind === 'none') return <span className="text-xs text-[var(--text-muted)]">—</span>;
    if (b.kind === 'not_applicable') {
      return (
        <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">
          対象外（{b.reason}）
        </span>
      );
    }
    return (
      <div className="flex flex-col items-start gap-0.5">
        <Badge variant={b.kind === 'paid' ? 'approved' : b.kind === 'billed' ? 'sent' : 'default'}>
          {BRIEFING_BILLING_LABEL[b.kind]}
        </Badge>
        {b.invoiceNumber && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setQInput(b.invoiceNumber ?? '');
              setParam('q', b.invoiceNumber ?? null);
            }}
            title="この請求書に含まれる個別説明だけを表示"
            className="font-mono text-[10px] text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-hover)]"
          >
            {b.invoiceNumber}
          </button>
        )}
      </div>
    );
  };

  const renderInvoiceBar = () => {
    if (!invoice) return null;
    const sum = invoiceBriefingSummary(invoice);
    const doneCount = visibleRows.filter((r) => r.state === 'completed').length;
    const months = [...new Set(visibleRows.map((r) => r.monthKey).filter(Boolean))]
      .sort()
      .map(monthLabel)
      .join('・');
    const label = (k: string) => (
      <span className="mr-1 text-[11px] text-[var(--text-muted)]">{k}</span>
    );
    return (
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-[var(--accent)] bg-[var(--accent-subtle)] px-4 py-2.5 text-[13px]">
        <span>
          {label('請求番号')}
          <span className="font-mono">{invoice.invoiceNumber}</span>
        </span>
        <span>
          {label('SA')}
          {invoice.recipientName || saName(invoice.recipientId)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          {label('状態')}
          <Badge
            variant={
              invoice.status === 'paid'
                ? 'approved'
                : invoice.status === 'sent'
                  ? 'sent'
                  : invoice.status === 'cancelled'
                    ? 'danger'
                    : 'default'
            }
          >
            {COMMISSION_INVOICE_STATUS_LABELS[invoice.status]}
          </Badge>
          <span className="text-[11px] tabular-nums text-[var(--text-muted)]">
            {fmtMd(invoice.issuedAt)} 発行
            {invoice.paidAt ? ` ・ ${fmtMd(invoice.paidAt)} 支払` : ''}
          </span>
        </span>
        <span className="tabular-nums">
          {label('個別説明代行')}
          {sum.quantity}件{sum.unitPrice !== null && ` × ¥${sum.unitPrice.toLocaleString()}`} ={' '}
          <b>¥{sum.amount.toLocaleString()}</b>
        </span>
        <span className="tabular-nums">
          {label('この一覧')}
          {visibleRows.length}件（実施済み {doneCount}）{months && `・${months}`}
        </span>
        <span className="ml-auto inline-flex items-center gap-1">
          <a
            href={`/c/sa-invoice/${invoice.publicToken}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-[var(--accent)] hover:bg-[var(--bg-hover)]"
          >
            <ExternalLink className="size-3.5" />
            請求書を開く
          </a>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setQInput('');
              setParam('q', null);
            }}
          >
            <X className="size-3.5" />
            解除
          </Button>
        </span>
      </div>
    );
  };

  const dim = invoice ? 'opacity-40 pointer-events-none' : '';

  return (
    <div className="space-y-3">
      {/* ── 絞り込み ── */}
      <div className="rounded-md border border-[var(--border)] p-3 space-y-2.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className={`flex items-center gap-1.5 ${dim}`}>
            <span className="text-xs text-[var(--text-muted)]">年月</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setParam('month', shiftMonth(month, -1))}
              title="前の月"
              aria-label="前の月"
            >
              <ChevronLeft />
            </Button>
            <Select value={month} onValueChange={(v) => setParam('month', v)}>
              <SelectTrigger className="h-8 w-[128px] text-xs" aria-label="年月">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {monthLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setParam('month', shiftMonth(month, 1))}
              title="次の月"
              aria-label="次の月"
            >
              <ChevronRight />
            </Button>
          </div>
          <div className={`flex items-center gap-1.5 ${dim}`}>
            <span className="text-xs text-[var(--text-muted)]">担当SA</span>
            <Select value={saFilter} onValueChange={(v) => setParam('sa', v)}>
              <SelectTrigger className="h-8 w-[170px] text-xs" aria-label="担当SA">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>すべて</SelectItem>
                {saOptions.map((o) => (
                  <SelectItem key={o.uid} value={o.uid}>
                    {o.label}
                  </SelectItem>
                ))}
                <SelectItem value={NONE}>未定</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative min-w-[220px] max-w-[340px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="請求番号・氏名・商談タイトルで探す"
              aria-label="請求番号・氏名・商談タイトルで探す"
              className="h-8 w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] pl-8 pr-2 text-xs text-[var(--text-primary)] focus:border-[var(--border-focus)] focus:outline-none"
            />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <span className="mr-1 text-xs text-[var(--text-muted)] whitespace-nowrap">
              表示中 <b className="tabular-nums text-[var(--text-primary)]">{visibleRows.length}</b>{' '}
              件
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={visibleRows.length === 0}
              title="表示中の行を CSV 書き出し"
            >
              <Download className="size-3.5 mr-1" />
              CSV
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => handleExport('xlsx')}
              disabled={visibleRows.length === 0}
              title="表示中の行を Excel 書き出し"
            >
              <Download className="size-3.5 mr-1" />
              Excel
            </Button>
          </div>
        </div>
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${dim}`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-[var(--text-muted)]">状態</span>
            {chip(stateFilter === ALL, 'すべて', baseRows.length, () => setParam('state', null))}
            {STATE_ORDER.map((s) =>
              chip(
                stateFilter === s,
                BRIEFING_ROW_STATE_LABEL[s],
                baseRows.filter((r) => r.state === s).length,
                () => setParam('state', s),
              ),
            )}
          </div>
          <span className="hidden h-5 w-px bg-[var(--border)] sm:block" aria-hidden />
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-[var(--text-muted)]">SA請求</span>
            {chip(billFilter === ALL, 'すべて', null, () => setParam('bill', null))}
            {BILL_ORDER.map((k) =>
              chip(
                billFilter === k,
                BRIEFING_BILLING_LABEL[k],
                baseRows.filter((r) => r.billing.kind === k).length,
                () => setParam('bill', k),
              ),
            )}
          </div>
        </div>
      </div>

      {/* ── 請求書で探している時の概要 / 担当SA別の件数 ── */}
      {invoice
        ? renderInvoiceBar()
        : saSummary.length > 0 && (
            <div
              className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-2"
              aria-label="担当SA別の件数 (押すとその担当SAに絞り込み)"
            >
              {saSummary.map(({ uid, rows }) => {
                const active = saFilter === uid;
                const done = rows.filter((r) => r.state === 'completed').length;
                const billed = rows.filter(
                  (r) => r.billing.kind === 'billed' || r.billing.kind === 'paid',
                ).length;
                const unbilled = rows.filter((r) => r.billing.kind === 'unbilled').length;
                // 代行料単価なしの社内スタッフ: 記録しても請求対象にならない
                const allInternal =
                  uid !== NONE &&
                  isInternal(uid) &&
                  (feeByUid.get(uid) ?? 0) <= 0 &&
                  unbilled === 0 &&
                  billed === 0;
                return (
                  <button
                    key={uid}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setParam('sa', active ? null : uid)}
                    className={`flex min-w-0 flex-col gap-0.5 rounded-sm border px-3 py-2 text-left transition-colors ${
                      active
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                        : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:border-[var(--accent)]'
                    }`}
                  >
                    <span className="flex items-baseline justify-between gap-2 text-[13px] font-semibold">
                      <span className="truncate">{uid === NONE ? '担当未定' : saName(uid)}</span>
                      <span className="text-[11px] font-normal tabular-nums text-[var(--text-muted)]">
                        {rows.length}件
                      </span>
                    </span>
                    <span className="text-[11px] tabular-nums text-[var(--text-secondary)]">
                      {uid === NONE ? (
                        `日程未確定 ${rows.length}`
                      ) : allInternal ? (
                        `実施 ${done}・請求の対象外（社内）`
                      ) : (
                        <>
                          実施 {done}・請求済 {billed}・
                          <span
                            className={
                              unbilled > 0 ? 'font-semibold text-[var(--accent-amber)]' : undefined
                            }
                          >
                            未請求 {unbilled}
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

      {/* ── 一覧 ── */}
      <div className="overflow-x-auto rounded-md border border-[var(--border)]">
        <div className="min-w-[1080px]" role="table" aria-label="個別説明の実施状況">
          <div
            role="row"
            className={`${GRID} border-b border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-2 text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]`}
          >
            <div role="columnheader">申込日</div>
            <div role="columnheader">予約日</div>
            <div role="columnheader">記録日</div>
            <div role="columnheader">氏名</div>
            <div role="columnheader">商談タイトル</div>
            <div role="columnheader">担当SA</div>
            <div role="columnheader">状態</div>
            <div role="columnheader">SA請求</div>
            <div role="columnheader">記録</div>
          </div>
          {visibleRows.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
              {invoice
                ? 'この請求書に含まれる個別説明は見つかりませんでした。'
                : '条件に合う個別説明はありません。年月や絞り込みを変えてください。'}
            </div>
          ) : (
            visibleRows.map((row, i) => {
              const canOpen = row.state !== 'unscheduled';
              return (
                <div
                  key={row.key}
                  id={`bs-row-${row.key}`}
                  role="row"
                  tabIndex={canOpen ? 0 : undefined}
                  onClick={canOpen ? () => openRecord(row) : undefined}
                  onKeyDown={
                    canOpen
                      ? (e) => {
                          if (e.key === 'Enter') openRecord(row);
                        }
                      : undefined
                  }
                  className={`${GRID} items-center px-4 py-2.5 transition-colors duration-700 ${
                    i < visibleRows.length - 1 ? 'border-b border-[var(--border)]' : ''
                  } ${canOpen ? 'group cursor-pointer hover:bg-[var(--bg-hover)]' : ''} ${
                    flashKey === row.key ? 'bg-[var(--accent-subtle)]' : ''
                  }`}
                >
                  <div className="text-xs tabular-nums text-[var(--text-secondary)]">
                    {fmt(row.appliedAt)}
                  </div>
                  <div className="text-xs tabular-nums text-[var(--text-secondary)]">
                    {fmt(row.scheduledAt)}
                  </div>
                  <div className="text-xs tabular-nums text-[var(--text-secondary)]">
                    {fmt(row.recordedAt)}
                  </div>
                  <div className="truncate text-sm font-medium" title={contactName(row.contactId)}>
                    {contactName(row.contactId)}
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="truncate text-xs text-[var(--text-secondary)]"
                      title={row.dealTitle}
                    >
                      {row.dealTitle}
                    </span>
                    {row.dealDeleted && <Badge variant="default">削除済みの商談</Badge>}
                  </div>
                  <div className="truncate text-xs">
                    {row.saUid ? (
                      <>
                        {saName(row.saUid)}
                        {isInternal(row.saUid) && (
                          <span className="ml-1 text-[10px] text-[var(--text-muted)]">社内</span>
                        )}
                      </>
                    ) : (
                      <span className="text-[var(--text-muted)]">未定</span>
                    )}
                  </div>
                  <div>
                    <Badge variant={STATE_BADGE[row.state]}>
                      {BRIEFING_ROW_STATE_LABEL[row.state]}
                    </Badge>
                  </div>
                  <div className="min-w-0">{renderBilling(row)}</div>
                  <div>
                    {row.state === 'unscheduled' ? (
                      <span className="text-xs text-[var(--text-muted)]">—</span>
                    ) : row.state === 'no_record' ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRecord(row);
                        }}
                      >
                        <FileText />
                        記録する
                      </Button>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-xs text-[var(--text-secondary)] group-hover:text-[var(--accent)]">
                        詳細
                        <ChevronRight className="size-3.5" />
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <p className="text-xs text-[var(--text-muted)]">
        {invoice && (
          <b className="text-[var(--text-secondary)]">
            請求番号で探しているときは、年月・担当SA・状態の絞り込みに関係なく、その請求書に含まれる個別説明をすべて表示します。{' '}
          </b>
        )}
        月の判定は<b className="text-[var(--text-secondary)]">予約日 → 申込日 → 記録日</b>
        の順です。<b className="text-[var(--text-secondary)]">記録日</b>
        は記録を最初に保存した日時（未実施の行は未実施にした日時）。状態は記録の内容に基づく表示で、システムが実施を確かめたものではありません。
      </p>
    </div>
  );
}
